import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { useAuth } from "@/context/auth/AuthContext";
import { useThemeConfig, DEFAULT_THEME } from "@/components/ui/theme-config-provider";
import usePremiumFeatures from "@/hooks/usePremiumFeatures";
import { getActiveThemes, getThemeConfig } from "@/services/theme.service";
import { AppTheme } from "@/types/theme";
import { useNavigate } from "react-router-dom";
import { Card } from "../ui/card";

interface ThemeOption extends AppTheme {
  primaryColor?: string;
  textColor?: string;
  textPrimaryColor?: string;
  backgroundColor?: string;
}

const FREE_THEME_IDS = [
  '11111111-1111-1111-1111-111111111111',
  '22222222-2222-2222-2222-222222222222',
  '55555555-5555-5555-5555-555555555555'
];

const SettingsThemeSection = () => {
  const { user } = useAuth();
  const { setThemeId, theme } = useThemeConfig();
  const { tryUseFeature } = usePremiumFeatures();
  const [themes, setThemes] = useState<ThemeOption[]>([]);
  const [selectedTheme, setSelectedTheme] = useState<string>('');

  useEffect(() => {
    const loadThemes = async () => {
      const activeThemes = await getActiveThemes();
      const themesWithConfig: ThemeOption[] = await Promise.all(
        activeThemes.map(async (t) => {
          const config = await getThemeConfig(t.id);
          return {
            ...t,
            primaryColor: config.primaryColor || DEFAULT_THEME.primaryColor,
            textColor: config.textColor || DEFAULT_THEME.textColor,
            textPrimaryColor:
              config.textPrimaryColor || DEFAULT_THEME.textPrimaryColor,
            backgroundColor:
              config.backgroundColor || DEFAULT_THEME.backgroundColor,
          };
        })
      );
      // Ordinamento personalizzato
      const sortedThemes = themesWithConfig.sort((a, b) => {
        const LIGHT_ID = '11111111-1111-1111-1111-111111111111';
        const DARK_ID = '22222222-2222-2222-2222-222222222222';

        if (a.id === LIGHT_ID) return -1;
        if (b.id === LIGHT_ID) return 1;
        if (a.id === DARK_ID) return -1;
        if (b.id === DARK_ID) return 1;

        return a.name.localeCompare(b.name);
      });
      setThemes(sortedThemes);
      const stored =
        localStorage.getItem('app_current_theme_id') ||
        '11111111-1111-1111-1111-111111111111';
      setSelectedTheme(stored);
    };
    loadThemes();
  }, []);


  const navigate = useNavigate();

  const handleThemeSelect = async (themeId: string) => {
    const isPremiumTheme = !FREE_THEME_IDS.includes(themeId);

    if (isPremiumTheme) {
      const allowed = tryUseFeature('custom_themes', { showToast: false });
      if (!allowed) {
        navigate('/premium');
        return;
      }
    }

    await setThemeId(themeId);
    setSelectedTheme(themeId);
  };

  return (
    <div
      className="rounded-lg shadow p-6 mb-6"
      style={{
        borderRadius: theme.borderRadius || "0.5rem",
        backgroundColor:
          theme.cardBackgroundColor || theme.backgroundColor || "#ffffff",
      }}
    >
      <h2
        className="text-xl font-semibold mb-4"
        style={{ color: theme.sectionTitleColor }}
      >
        Aspetto
      </h2>

      <div
        className="mt-4 pt-4 border-t"
        style={{ borderColor: theme.borderColor }}
      >
        <h3
          className="text-lg font-medium mb-3"
          style={{ color: theme.sectionTitleColor }}
        >
          Temi disponibili
        </h3>
        {/* Griglia a due colonne per i temi, responsive anche su mobile */}
        <div className="grid grid-cols-2 gap-2">
          {themes.map((t) => {
            const isSelected = selectedTheme === t.id;

            const buttonColor =
              t.primaryColor || DEFAULT_THEME.primaryColor;

            const buttonTextColor =
              t.textPrimaryColor || DEFAULT_THEME.textPrimaryColor;

            const isPremiumTheme = !FREE_THEME_IDS.includes(t.id);

            return (
              <Card
                key={t.id}
                className="relative overflow-hidden"
                style={{
                  borderRadius: theme.cardBorderRadius || theme.borderRadius,
                  boxShadow: theme.cardShadow,
                  borderColor: isSelected ? theme.primaryColor : theme.borderColor,
                  borderWidth: isSelected ? '2px' : '1px',
                  height: '65px',
                }}
              >
                {isPremiumTheme && (
                  <Badge
                    variant="secondary"
                    className="absolute bottom-1 right-2 text-xs"
                    style={{
                      backgroundColor: DEFAULT_THEME.primaryColor,
                      color: DEFAULT_THEME.textPrimaryColor,
                      borderRadius: theme.badgeBorderRadius,
                      padding: theme.badgePadding,
                      fontSize: theme.badgeFontSize,
                    }}
                  >
                    Premium
                  </Badge>
                )}
                {/* Il bottone occupa l'intera card per facilitare il tap su mobile */}
                <Button
                  variant="ghost"
                  aria-label={`Seleziona tema ${t.name}`}
                  className="w-full h-full rounded-2xl flex items-start justify-center py-4 gap-2 text-center disabled:opacity-50"
                  onClick={() => handleThemeSelect(t.id)}
                  style={{
                    backgroundColor: buttonColor,
                    color: buttonTextColor,
                    borderRadius: theme.buttonBorderRadius,
                    padding: theme.buttonPadding,
                    fontWeight: theme.buttonFontWeight || 'bold',
                  }}
                >
                  {isSelected && <Check className="h-4 w-4 shrink-0" />}
                  <span className="break-words text-sm leading-tight text-left">
                    {t.name}
                  </span>
                </Button>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SettingsThemeSection;
